import { ReplicantError } from '../Errors';
import type { Replicant } from '../ReplicantConfig';
import type { Ruleset } from '../ReplicantRuleset';
import type { FriendsStatesMap } from '../common/FriendsStatesMap';
import type { Entry } from '../db/DB';
import { addSystemStateFields, getSystemStateFieldsFromEntry } from '../systemStateFields';
import { isAndroidDeviceTokenExpired, isAppleDeviceTokenExpired } from '../utils/DeviceTokenUtils';
import type Metrics from './Metrics';
import type { APIAccessInterface } from './ServerReplicant';

export default class ServerReplicantFetchStates<T extends Replicant> {
    constructor(
        private opts: {
            apiAccess: APIAccessInterface<T>;
            gzipToBase64: (input: string) => string;
            metrics?: Metrics;
        },
    ) {}

    /**
     * @param disablePagination By default results are paginated in batches of
     * about 1MB to accommodate the Lambda/ALB response size limit; use this
     * option to disable pagination.
     */
    async fetchStates(
        ids: string[],
        opts: {
            friendRevs?: { [id: string]: number };
            consistentFetchIds: string[];
            disableDefaultEntryCreation?: boolean;
            disablePagination?: boolean;
        },
    ): Promise<{
        states: FriendsStatesMap<T['state'], T['ruleset'], T['sharedStates']>;
        unprocessedIds?: string[];
    }> {
        const { apiAccess, metrics } = this.opts;

        ids = Array.from(new Set(ids.filter((x) => !!x)));

        if (ids.length === 0) {
            return { states: {} };
        }

        // Track consistent/inconsistent read counts:
        if (metrics) {
            const consistentReadsCount = ids.filter((id) => opts.consistentFetchIds.includes(id)).length;
            if (consistentReadsCount) {
                metrics.pushCustomMetric('fetchStates.consistentReadsCount', consistentReadsCount);
            }

            const inconsistentReadsCount = ids.length - consistentReadsCount;
            if (inconsistentReadsCount) {
                metrics.pushCustomMetric('fetchStates.inconsistentReadsCount', inconsistentReadsCount);
            }
        }

        const res: FriendsStatesMap<T['state'], T['ruleset']> = {};

        const entryResults = await Promise.all(
            ids.map((id) =>
                apiAccess
                    .getReadOnlyEntry(id, {
                        consistentRead: opts.consistentFetchIds.includes(id),
                        createIfNotFound: true,
                    })
                    .catch(async (error) => {
                        const dbReadFailed = error instanceof ReplicantError && error.subCode === 'db_error';

                        if (dbReadFailed) {
                            // Report warning and exclude user from results if DB read failed:
                            const warning = new ReplicantError(
                                `Fetching state error for ${id}: ${error.message}`,
                                'server_error',
                                'db_error',
                                'warning',
                            );
                            warning.stack = error.stack;

                            await apiAccess.reportError(warning);
                        } else {
                            // Throw in case of other errors like invalid state:
                            throw error;
                        }
                    }),
            ),
        );

        for (let i = 0; i < ids.length; i++) {
            const id = ids[i]!;
            const entryResult = entryResults[i];

            if (!entryResult) {
                continue;
            }

            const { entry, messagesCount } = entryResult;

            const appleDeviceTokenExpired = entry.chatbotMetainfo?.appleDeviceTokenUpdatedAt
                ? isAppleDeviceTokenExpired(entry.chatbotMetainfo.appleDeviceTokenUpdatedAt, apiAccess.dateNow())
                : false;
            const androidDeviceTokenExpired = entry.chatbotMetainfo?.androidDeviceTokenUpdatedAt
                ? isAndroidDeviceTokenExpired(entry.chatbotMetainfo.androidDeviceTokenUpdatedAt, apiAccess.dateNow())
                : false;
            const friendRev = getCompositeRevId(entry, messagesCount);

            if (!opts.friendRevs || !(id in opts.friendRevs) || opts.friendRevs[id] !== friendRev) {
                addSystemStateFields(entry.state, getSystemStateFieldsFromEntry(id, entry), apiAccess.config);

                res[id] = {
                    state: entry.state,
                    lastLoginAt: entry.lastLoginAt,
                    lastUpdated: entry.lastUpdated,
                    createdAt: entry.createdAt,
                    friendRev,
                    apnEnabled: !!entry.chatbotMetainfo?.appleDeviceToken && !appleDeviceTokenExpired,
                    fcmEnabled: !!entry.chatbotMetainfo?.androidDeviceToken && !androidDeviceTokenExpired,
                    chatbot: { subscribed: !!entry.chatbotMetainfo?.subscribed },
                    metainfo: { abTestAssignments: entry.metainfo.abTestAssignments },
                    userSharedStates: entry.userSharedStates,
                };
            }
        }

        // If the result is more than 1MB, we need to return only those states that fit into a meg and then a list
        // of unprocessed ids, so they client can re-request them.

        const MAX_SIZE = 950000;

        if (opts.disablePagination) {
            return { states: res };
        } else {
            return paginateStates({ allStates: res, gzipToBase64: this.opts.gzipToBase64, maxSize: MAX_SIZE });
        }
    }
}

function getCompositeRevId(entry: Entry<any>, messagesCount: number): number {
    return entry.rev * 10000 + messagesCount;
}

export function paginateStates<TUserState, TRuleset extends Ruleset>(opts: {
    allStates: FriendsStatesMap<TUserState, TRuleset>;
    maxSize: number;
    gzipToBase64: (input: string) => string;
}): { states: FriendsStatesMap<TUserState, TRuleset>; unprocessedIds?: string[] } {
    const { allStates, gzipToBase64, maxSize } = opts;

    if (gzipToBase64(JSON.stringify(allStates)).length < maxSize) {
        return { states: allStates };
    } else {
        // Binary search to fit `n` first states into our result object:
        const resIds = Object.keys(allStates);
        let min = 1;
        let max = resIds.length;
        let n = Math.floor((min + max) / 2);
        let states: FriendsStatesMap<TUserState, TRuleset> = resIds
            .slice(0, n)
            .reduce((acc, id) => ({ ...acc, [id]: allStates[id] }), {});

        while (n !== min && n !== max) {
            const currentSize = gzipToBase64(JSON.stringify(states)).length;
            if (currentSize > maxSize) {
                max = n;
                n = Math.floor((min + n) / 2);
            } else {
                min = n;
                n = Math.floor((n + max) / 2);
            }

            states = resIds.slice(0, n).reduce((acc, id) => ({ ...acc, [id]: allStates[id] }), {});
        }

        return {
            states,
            unprocessedIds: resIds.slice(n),
        };
    }
}
